import { z } from 'zod';
import {
  Account,
  AssetType,
  ProductFamily,
} from '../../../../../../../types/api';
import { LIMITS, UNITED_STATES } from '../../../../constants';
import { Asset } from '../../../../modules/loan/asset';
import { LoanApplication } from '../../../../modules/loan/application';
import { Installer } from '../../../../modules/installer';
const { BorrowerForm } = Installer.UI;
import { safeJsonParse } from '../../../../utils/helpers';
import { currencyFormatter } from '../../../../utils/formatters';

export const checkAtLeastOneCheckbox = (obj) => {
  return Object.values(obj).some((val) => val === true);
};

const nameHasNoNewSpaces = (value: string, savedName?: string) =>
  !value.includes(' ') || value === savedName;

export const borrowerContactSchema = (
  signedInEmail: string,
  savedName?: { firstName?: string; lastName?: string }
) =>
  z.object({
    first_name: z
      .string()
      .refine((value) => value !== '', "Contact's First name is required")
      .refine(
        (value) => nameHasNoNewSpaces(value, savedName?.firstName),
        'First name cannot contain spaces'
      ),
    last_name: z
      .string()
      .refine((value) => value !== '', "Contact's Last name is required")
      .refine(
        (value) => nameHasNoNewSpaces(value, savedName?.lastName),
        'Last name cannot contain spaces'
      ),
    position_title: z
      .string()
      .refine((value) => value !== '', "Contact's Position/Title is required")
      .refine(
        (value) => value.length <= 40,
        'Position title is too long. Please limit it to 40 characters.'
      ),
    email: z
      .string()
      .email('Must be a valid email address')
      .refine((value) => value !== '', "Contact's Email address is required")
      .refine(
        (value) => value !== signedInEmail,
        (value) => ({
          message: `${value} can not be used to initialize this loan application. Please enter another email.`,
        })
      ),
    phone: z
      .string()
      .nullable()
      .refine(
        (value) => value === null || /^[0-9]+$/.test(value),
        'Please enter numbers only (no letters, spaces, or special characters)'
      )
      .refine(
        (value) => value === null || value.length === 10,
        'Must be 10 digits'
      )
      .refine(
        (value) => value !== null && value !== '',
        'A phone number is required'
      ),
  });

export const getValidationSchema = (
  email: string,
  requireBorrowerAdvance: boolean,
  isBorrowerFormV2Active = false,
  account: Account | null | undefined = null,
  selectedAssetTypes: ReadonlyArray<AssetType> = []
) => {
  // System size, year-1 figures, and mounting hardware live in the Solar
  // Project Information section, which is hidden for Flex-only projects.
  // Require them only when that section is visible — the field-level
  // refines below short-circuit when it isn't.
  const solarFieldsRequired =
    BorrowerForm.isSolarProjectInformationVisible(selectedAssetTypes);
  const minLoanAmount = LoanApplication.minLoanAmount(selectedAssetTypes);

  const validationSchema = z
    .object({
      borrower: z.object({
        name: z
          .string()
          .refine((value) => value !== '', 'Business name is required'),
      }),
      loanType: z
        .nativeEnum(ProductFamily, {
          invalid_type_error: 'Please select a loan type',
        })
        .nullable()
        .optional()
        .refine((value) => Boolean(value), 'Please select a loan type'),
      loanTerm: z
        .string()
        .min(1, 'Please select a loan term')
        .optional()
        .refine((value) => Boolean(value), 'Please select a loan term'),
      contact: borrowerContactSchema(email),
      application: z
        .object({
          dealer_fee: z.string().nullable().optional(),
          sys_prop_address_state: z
            .string()
            .refine(
              (value) =>
                UNITED_STATES.map((state) => state.value).includes(value),
              "The property's State where the system will be installed must be one of the valid options"
            )
            .refine(
              (value) => value !== '',
              "The property's State where the system will be installed is required"
            ),
          rec_info: z.string().nullable().optional(),
          loan_product: z
            .string()
            .refine((value) => value !== '', 'Please select the interest rate'),
          has_non_solar_component: z.union([
            z.literal('Yes'),
            z.literal('No'),
            z.literal(''),
          ]),
          non_solar_amount: z
            .union([z.string().length(0), z.coerce.number()])
            .nullable()
            .optional(),
          energy_storage_amount: z
            .union([z.string().length(0), z.coerce.number()])
            .nullable()
            .optional(),
          ev_charging_amount: z
            .union([z.string().length(0), z.coerce.number()])
            .nullable()
            .optional(),
          roof_repair_amount: z
            .union([z.string().length(0), z.coerce.number()])
            .nullable()
            .optional(),
          other_non_solar_amount: z
            .union([z.string().length(0), z.coerce.number()])
            .nullable()
            .optional(),
          system_mounting_hardware: z.string().optional(),
          non_solar_components: z.string().optional(),
          loan_amount: z
            .union([
              z.coerce
                .number()
                .refine(
                  (value) => value >= minLoanAmount,
                  `Must be at least ${currencyFormatter.format(minLoanAmount)}`
                )
                .refine(
                  (value) => value <= LIMITS.MAX_LOAN_AMOUNT,
                  `Cannot exceed ${currencyFormatter.format(
                    LIMITS.MAX_LOAN_AMOUNT
                  )}`
                ),
              z.string().length(0),
            ])
            .refine(
              (value) => typeof value === 'number',
              'Loan amount is required'
            ),
          net_funding: z
            .union([z.coerce.number(), z.string().length(0)])
            .optional()
            .nullable(),
          project_timeline_estimation: z
            .string()
            .refine(
              (value) => value !== '',
              'Project Timeline Estimation is required'
            ),
          deal_description_and_context: z.string().max(5000),
          system_size_in_k_ws: z.coerce
            .number()
            .nullable()
            .refine((value) => !solarFieldsRequired || value !== null, {
              message: 'System Size in kW is required',
            })
            .refine(
              (value) => !solarFieldsRequired || value === null || value > 0,
              { message: 'Must be greater than 0' }
            ),
          year_1_system_production_kwh: z.coerce
            .number()
            .nullable()
            .refine((value) => !solarFieldsRequired || value !== null, {
              message: 'Year 1 Solar System Production (kWh) is required',
            })
            .refine(
              (value) => !solarFieldsRequired || value === null || value > 0,
              { message: 'Must be greater than 0' }
            ),
          year_1_total_solar_savings: z.coerce
            .number()
            .nullable()
            .refine((value) => !solarFieldsRequired || value !== null, {
              message: 'Year 1 Total Solar Savings is required',
            })
            .refine(
              (value) => !solarFieldsRequired || value === null || value > 0,
              { message: 'Must be greater than 0' }
            ),
          total_system_cost: z.coerce.number().nullable().optional(),
          is_special: z.string().nullable().optional(),
          special_payment_percentage: z.coerce.number().nullable().optional(),
          borrower_advance: z.string().nullable().optional(),
          is_flagged_non_profit_by_installer: z
            .string()
            .refine((value) => value !== '', 'Choice is required'),
          external_source_id: z.string().nullable().optional(),
          interest_payer_during_construction: z.string().nullable().optional(),
          project_nickname: z.string().max(255).nullable().optional(),
        })
        .superRefine((values, context) => {
          // Only require borrower_advance if conditions are met
          if (
            requireBorrowerAdvance &&
            (!values?.borrower_advance || values.borrower_advance === '')
          ) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Answer is required',
              path: ['borrower_advance'],
            });
          }
          if (
            isBorrowerFormV2Active &&
            requireBorrowerAdvance &&
            values?.borrower_advance &&
            values.borrower_advance !== 'No' &&
            BorrowerForm.showsConstructionInterestPayer(selectedAssetTypes) &&
            (!values?.interest_payer_during_construction ||
              values.interest_payer_during_construction === '')
          ) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Answer is required',
              path: ['interest_payer_during_construction'],
            });
          }
          // Only require total_system_cost if conditions are met
          if (isBorrowerFormV2Active && values?.total_system_cost === null) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Total System Cost is required',
              path: ['total_system_cost'],
            });
          }
          if (
            isBorrowerFormV2Active &&
            values?.total_system_cost &&
            values.total_system_cost <= 0
          ) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Must be greater than 0',
              path: ['total_system_cost'],
            });
          }
        }),
      loan_proposal: z.object({
        file: z.instanceof(File, {
          message: 'Proposal document is required',
        }),
      }),
      utility_bills: z.array(
        z.object({
          file: z.instanceof(File, {
            message: 'Utility bill file is required',
          }),
        })
      ),
      incentive_documents: z.array(
        z.object({
          file: z.instanceof(File, {
            message: 'Incentive document file is required',
          }),
        })
      ),
      mounting: z
        .object({
          roof: z.boolean(),
          ground: z.boolean(),
          carport: z.boolean(),
          other: z.boolean(),
          otherValue: z.string().optional(),
        })
        .refine((value) => (value.other ? Boolean(value.otherValue) : true), {
          message: 'Enter Other Mounting Hardware',
          path: ['otherValue'],
        })
        .optional(),
      mountingChecked: z.coerce.boolean().optional(),
      nonSolar: z
        .object({
          hasNonSolarComponent: z
            .boolean()
            .optional()
            .refine(
              (value) => typeof value === 'boolean',
              'Non-Solar Project Component status is required'
            ),
          nonSolarAmount: z.coerce.number().optional(),
          energyStorage: z.boolean(),
          evCharging: z.boolean(),
          roofRepair: z.boolean(),
          other: z.boolean(),
          otherValue: z.string().optional(),
        })
        .refine(
          (value) =>
            value.hasNonSolarComponent ? Boolean(value.nonSolarAmount) : true,
          {
            message:
              'Enter Estimated total cost of non-solar project components',
            path: ['nonSolarAmount'],
          }
        )
        .refine((value) => (value.other ? Boolean(value.otherValue) : true), {
          message: 'Enter Other Component',
          path: ['otherValue'],
        })
        .optional(),

      nonSolarChecked: z.coerce.boolean().optional(),

      projectAssets: z
        .object({
          solar: z.boolean(),
          energyStorage: z.boolean(),
          evChargers: z.boolean(),
          roofing: z.boolean(),
          hvac: z.boolean(),
          ledLighting: z.boolean(),
          other: z.boolean(),
          otherValue: z.string().optional(),
          otherPrice: z.preprocess(
            (v) => (v === null || v === '' ? undefined : v),
            z.coerce.number().optional()
          ),
        })
        .refine((value) => (value.other ? Boolean(value.otherValue) : true), {
          message: 'Enter Other improvement description',
          path: ['otherValue'],
        })
        .optional(),

      projectAssetsChecked: z.coerce.boolean().optional(),
    })
    .refine(
      (values) => {
        const {
          hasNonSolarComponent,
          nonSolarAmount,
          otherValue,
          ...nonSolarCheckboxes
        } = values.nonSolar ?? {};
        return hasNonSolarComponent
          ? checkAtLeastOneCheckbox(nonSolarCheckboxes)
          : true;
      },
      { message: 'Check at least one checkbox', path: ['nonSolarChecked'] }
    )
    .refine(
      (values) => {
        // Hidden card list (one allowed asset) has nothing to validate.
        if (!BorrowerForm.isProjectAssetInformationSectionVisible(account))
          return true;
        const { otherValue, otherPrice, ...projectAssetsCheckboxes } =
          values.projectAssets ?? {};
        return checkAtLeastOneCheckbox(projectAssetsCheckboxes);
      },
      {
        message: 'Check at least one improvement',
        path: ['projectAssetsChecked'],
      }
    )
    .superRefine((values, context) => {
      // Mounting hardware lives in the Solar Project Information section —
      // only require a selection when that section is visible. Flex-only
      // projects hide it.
      const selectedAssetTypes = Asset.getSelectedTypes(values.projectAssets);
      if (
        BorrowerForm.isSolarProjectInformationVisible(selectedAssetTypes) &&
        !checkAtLeastOneCheckbox(values.mounting)
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Check at least one checkbox',
          path: ['mountingChecked'],
        });
      }
    })
    .superRefine((values, context) => {
      const state = values.application?.sys_prop_address_state;
      if (!['Colorado', 'Illinois', 'Massachusetts'].includes(state)) return;
      const selectedAssetTypes = Asset.getSelectedTypes(values.projectAssets);
      if (!BorrowerForm.isSolarProjectInformationVisible(selectedAssetTypes)) {
        return;
      }
      if (!values.application?.rec_info) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'You must choose a Utility ' +
            (state === 'Massachusetts' ? 'Territory' : 'Company'),
          path: ['application', 'rec_info'],
        });
      }
    })
    .superRefine((values, context) => {
      // Utility bills are required for any project where the section
      // renders. Flex-only projects skip the requirement because the
      // section itself is hidden — UI + validator stay in lockstep
      // via the shared FormRules helper.
      const selectedAssetTypes = Asset.getSelectedTypes(values.projectAssets);
      if (!BorrowerForm.isUtilityBillsRequired(selectedAssetTypes)) {
        return;
      }
      if (!values.utility_bills || values.utility_bills.length < 1) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'At least one utility bill is required',
          path: ['utility_bills'],
        });
      }
    });
  return validationSchema;
};

export const timelineEstimations = [
  {
    label: 'Already complete',
    value: 'The project is already complete',
  },
  {
    label: 'Currently under construction',
    value: 'The project is currently under construction',
  },
  {
    label: 'Not started — completion within 3 months',
    value: 'Construction has not started, expect completion within 3 months',
  },
  {
    label: 'Not started — completion within 6 months',
    value: 'Construction has not started, expect completion within 6 months',
  },
  {
    label: 'Not started — completion in 6+ months',
    value:
      'Construction has not started, expect completion in more than 6 months',
  },
  {
    label: 'Not started — timeline unknown',
    value:
      'Construction has not started, construction timeline currently unknown',
  },
];

export type InitialValues = z.infer<ReturnType<typeof getValidationSchema>>;

// Only 5 values are allowed: Standard, Special, Tax Credit Transfer, Flex and SOL Standard
enum PrefillProductFamily {
  STANDARD = 'Standard',
  SPECIAL_PAYMENT = 'Special',
  TAX_CREDIT = 'Tax Credit Transfer',
  FLEX = 'Flex',
  STORAGE = 'SOL Standard',
}

function toTitleCase(str: string) {
  return str
    .split(' ')
    .map((word) => {
      if (word.length === 0) {
        return ''; // Handle empty words resulting from multiple spaces
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

export const NewBorrowerUrlParamsSchema = z.object({
  loanType: z.preprocess(
    (value) => (typeof value === 'string' ? toTitleCase(value) : value),
    z
      .nativeEnum(PrefillProductFamily)
      .optional()
      .transform((value, ctx) => {
        switch (value) {
          case 'Special':
            return ProductFamily.SPECIAL_PAYMENT;
          case 'Tax Credit Transfer':
            return ProductFamily.TAX_CREDIT;
          case 'Standard':
            return ProductFamily.STANDARD;
          case 'Flex':
            return ProductFamily.FLEX;
          default:
            return undefined;
        }
      })
  ),

  loanYears: z.coerce.number().optional(),
  amortizationYears: z.coerce.number().optional(),
  interestRate: z.coerce.number().optional(),
  loanAmount: z.coerce.number().optional(),
  milestonePayments: z.enum(['Yes', 'No']).optional(),
  specialPaymentPercentage: z.coerce.number().optional(),
  businessName: z.string().optional(),
  isNonProfit: z
    .string()
    .optional()
    .transform((value) => {
      if (!value) return undefined;
      const normalized = value.toLowerCase();
      if (normalized === 'yes') return 'Yes';
      if (normalized === 'no') return 'No';
      return undefined;
    }),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  title: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  state: z
    .string()
    .optional()
    .transform((value) => {
      if (!value) return undefined;

      const state = UNITED_STATES.find(
        (state) =>
          state.value.toLowerCase() === value.toLowerCase() ||
          state.abbreviation.toLowerCase() === value.toLowerCase()
      );
      return state?.value;
    }),
  timeline: z.string().optional(),
  systemSizeKw: z.coerce.number().optional(),
  year1ProductionKwh: z.coerce.number().optional(),
  year1SavingsUsd: z.coerce.number().optional(),
  systemCostUsd: z.coerce.number().optional(),
  mountingType: z.string().optional(),
  includesEnhancements: z
    .string()
    .optional()
    .transform((value) => value === 'true'),
  nonSolarCost: z.coerce.number().optional(),
  fundedEnhancements: z.string().optional(),
  enhancementCosts: z
    .string()
    .optional()
    .transform((value) => safeJsonParse(value) ?? {}),
  dealContext: z.string().optional(),
  externalId: z.string().optional(),

  // Backward compatibility fields
  productId: z.string().optional(),
  y1production: z.coerce.number().optional(),
  y1saving: z.coerce.number().optional(),
  systemLocation: z.string().optional(),
  systemSize: z.coerce.number().optional(),
  recInfo: z.string().optional(),
  isSpecial: z.string().optional(),
  projectAssets: z
    .string()
    .optional()
    .transform((value) => safeJsonParse(value) ?? undefined),
});

export type NewBorrowerUrlParams = z.infer<typeof NewBorrowerUrlParamsSchema>;

export const safeParseNewBorrowerUrlParams = (params: URLSearchParams) => {
  const parsedParams = NewBorrowerUrlParamsSchema.safeParse(
    Object.fromEntries(params.entries())
  );
  if (parsedParams.success) return parsedParams.data;
  return null;
};
